import { supabase } from '@/shared/config/supabase';
import { IAcademicCyclesRepository } from '@/shared/application/use-cases/GetAcademicCyclesUseCase';
import { AcademicCycleDTO } from '@/shared/application/dto/AcademicCycleDTO';

/**
 * Supabase Academic Cycles Repository
 *
 * Implements academic cycles data access using Supabase
 */
export class SupabaseAcademicCyclesRepository implements IAcademicCyclesRepository {
  /**
   * Get all academic cycles, optionally filtered by workspace
   */
  async getAll(workspaceId?: number): Promise<AcademicCycleDTO[]> {
    let query = supabase
      .from('AcademicCycles')
      .select('*')
      .order('start_date', { ascending: false });

    if (workspaceId !== undefined) {
      query = query.eq('workspace_id', workspaceId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching academic cycles:', error);
      throw new Error(`Failed to fetch academic cycles: ${error.message}`);
    }

    return (data || []).map(this.mapToDTO);
  }

  /**
   * Get only active academic cycles
   */
  async getActive(workspaceId?: number): Promise<AcademicCycleDTO[]> {
    let query = supabase
      .from('AcademicCycles')
      .select('*')
      .eq('status', true)
      .order('start_date', { ascending: false });

    if (workspaceId !== undefined) {
      query = query.eq('workspace_id', workspaceId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching active cycles:', error);
      throw new Error(`Failed to fetch active cycles: ${error.message}`);
    }

    return (data || []).map(this.mapToDTO);
  }

  /**
   * Get current academic cycle (active and within date range)
   */
  async getCurrent(workspaceId?: number): Promise<AcademicCycleDTO | null> {
    const now = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

    let query = supabase
      .from('AcademicCycles')
      .select('*')
      .eq('status', true)
      .lte('start_date', now)
      .gte('end_date', now)
      .order('start_date', { ascending: false })
      .limit(1);

    if (workspaceId !== undefined) {
      query = query.eq('workspace_id', workspaceId);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching current cycle:', error);
      throw new Error(`Failed to fetch current cycle: ${error.message}`);
    }

    if (!data || data.length === 0) {
      return null;
    }

    return this.mapToDTO(data[0]);
  }

  /**
   * Get academic cycle by ID
   */
  async getById(id: number): Promise<AcademicCycleDTO | null> {
    const { data, error } = await supabase
      .from('AcademicCycles')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) {
      return null;
    }

    return this.mapToDTO(data);
  }

  /**
   * Create a new academic cycle
   */
  async create(data: {
    workspaceId: number;
    name: string;
    startDate: string;
    endDate: string;
    status: boolean;
  }): Promise<AcademicCycleDTO> {
    const { data: cycle, error } = await supabase
      .from('AcademicCycles')
      .insert({
        workspace_id: data.workspaceId,
        name: data.name,
        start_date: data.startDate,
        end_date: data.endDate,
        status: data.status,
      })
      .select()
      .single();

    if (error || !cycle) {
      throw new Error(`Error al crear ciclo académico: ${error?.message}`);
    }

    return this.mapToDTO(cycle);
  }

  /**
   * Update an existing academic cycle
   */
  async update(
    id: number,
    data: {
      name?: string;
      startDate?: string;
      endDate?: string;
      status?: boolean;
    }
  ): Promise<AcademicCycleDTO> {
    const updateData: Record<string, unknown> = {};
    if (data.name !== undefined) updateData.name = data.name;
    if (data.startDate !== undefined) updateData.start_date = data.startDate;
    if (data.endDate !== undefined) updateData.end_date = data.endDate;
    if (data.status !== undefined) updateData.status = data.status;

    const { data: cycle, error } = await supabase
      .from('AcademicCycles')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error || !cycle) {
      throw new Error(`Error al actualizar ciclo académico: ${error?.message}`);
    }

    return this.mapToDTO(cycle);
  }

  /**
   * Delete an academic cycle
   * Validates that the cycle has no associated courses
   */
  async delete(id: number): Promise<void> {
    // Check if cycle has courses
    const { count: courseCount } = await supabase
      .from('Courses')
      .select('*', { count: 'exact', head: true })
      .eq('academic_cycle_id', id);

    if (courseCount && courseCount > 0) {
      throw new Error('No se puede eliminar un ciclo que tiene cursos asociados');
    }

    const { error } = await supabase.from('AcademicCycles').delete().eq('id', id);

    if (error) {
      throw new Error(`Error al eliminar ciclo: ${error.message}`);
    }
  }

  /**
   * Toggle the status of an academic cycle
   * Uses RPC function to avoid CORS issues with PATCH requests
   */
  async toggleStatus(id: number): Promise<AcademicCycleDTO> {
    const { data, error } = await supabase.rpc('toggle_academic_cycle_status', {
      cycle_id: id,
    });

    if (error) {
      // Handle specific error messages from the RPC function
      if (error.message.includes('No tienes permisos')) {
        throw new Error('No tienes permisos para modificar ciclos académicos. Contacta al administrador.');
      }
      if (error.message.includes('Ciclo no encontrado')) {
        throw new Error('Ciclo no encontrado');
      }
      if (error.message.includes('No tienes acceso')) {
        throw new Error('No tienes acceso a este ciclo académico');
      }
      throw new Error(`Error al cambiar estado del ciclo: ${error.message}`);
    }

    if (!data) {
      throw new Error('No se recibió respuesta del servidor');
    }

    return {
      id: data.id,
      workspaceId: data.workspace_id,
      name: data.name,
      startDate: data.start_date,
      endDate: data.end_date,
      status: data.status,
    };
  }

  /**
   * Map database row to DTO
   */
  private mapToDTO(row: { id: number; workspace_id: number; name: string; start_date: string; end_date: string; status: boolean }): AcademicCycleDTO {
    return {
      id: row.id,
      workspaceId: row.workspace_id,
      name: row.name,
      startDate: row.start_date,
      endDate: row.end_date,
      status: row.status,
    };
  }
}
